import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Card, Input, Select, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import { relativeTime } from '@/lib/format';
import { createTwimlApp, getPhoneStatus, listPhoneNumbers, syncPhoneNumbers, updatePhoneNumber } from '@/data/client';
import { useMembers } from '@/data/hooks';
import { useMe } from '@/auth/useMe';
import type { PhoneNumber } from '@/data/types';
import { FormError } from '@/components/crm/Modal';

const PURPOSES = [{ value: 'test', label: 'Test line' }, { value: 'main', label: 'Main line' }, { value: 'support', label: 'Customer service' }, { value: 'cold', label: 'Cold calls' }];

/* Settings → Phone (admins): the Twilio secrets' status, the account's numbers
   pulled in and pointed at this Worker, and what each number says and who it
   rings. The secrets themselves live in the Worker; this page only reports them. */
export function PhoneSettings() {
  const { me } = useMe();
  const qc = useQueryClient();
  const { data: status } = useQuery({ queryKey: ['phoneStatus'], queryFn: getPhoneStatus });
  const { data: numbers = [] } = useQuery({ queryKey: ['phoneNumbers'], queryFn: listPhoneNumbers, enabled: !!status?.configured });
  const refresh = () => { for (const k of ['phoneStatus', 'phoneNumbers']) void qc.invalidateQueries({ queryKey: [k] }); };
  const sync = useMutation({ mutationFn: syncPhoneNumbers, onSuccess: refresh });
  const twiml = useMutation({ mutationFn: createTwimlApp });
  if (!status) return null;
  const base = window.location.origin;
  return (
    <div className="crm-list">
      <Card padding={false} className="crm-section crm-settings-card">
        <div className="crm-section__head"><h3>Twilio</h3><Badge variant={status.browserCalling ? 'success' : status.configured ? 'warning' : 'neutral'}>{status.browserCalling ? 'Ready' : status.configured ? 'Almost there' : 'Not set up'}</Badge></div>
        <p className="crm-cell-muted">
          Calls from Triton's numbers happen in the browser and are transcribed live into the account. No audio is kept. Secrets go in the Worker
          (Cloudflare → clientiq → Settings → Variables and Secrets); this page reads which are set.
        </p>
        <ul className="crm-checks">
          <li>{status.configured ? <Icon name="check-circle" size={15} /> : <Icon name="alert-circle" size={15} />} <code>TWILIO_ACCOUNT_SID</code> and <code>TWILIO_AUTH_TOKEN</code> {status.configured ? 'set' : 'missing'}</li>
          <li>{status.browserCalling || status.twimlAppSet ? <Icon name="check-circle" size={15} /> : <Icon name="alert-circle" size={15} />} <code>TWILIO_TWIML_APP_SID</code> {status.twimlAppSet ? 'set' : 'missing'}</li>
          <li>{status.browserCalling ? <Icon name="check-circle" size={15} /> : <Icon name="alert-circle" size={15} />} <code>TWILIO_API_KEY_SID</code> and <code>TWILIO_API_KEY_SECRET</code> {status.browserCalling ? 'set' : status.twimlAppSet || !status.configured ? 'missing or incomplete' : 'missing'}</li>
        </ul>
        {status.configured && !status.twimlAppSet && (
          <div className="crm-form">
            <p className="crm-cell-muted">Outbound browser calls go through a TwiML App that points at this site. Create it here, then add its SID as the <code>TWILIO_TWIML_APP_SID</code> secret.</p>
            <div><Button size="sm" variant="outline" onClick={() => twiml.mutate()} disabled={twiml.isPending}>{twiml.isPending ? 'Creating…' : 'Create the TwiML App'}</Button></div>
            {twiml.data && <p className="crm-callout">TwiML App SID: <code>{twiml.data.sid}</code> — add it as <code>TWILIO_TWIML_APP_SID</code>. The Worker redeploys when the secret is saved.</p>}
            <FormError error={twiml.error} />
          </div>
        )}
        <p className="crm-cell-sub">Webhooks this site answers: <code>{base}/twilio/voice/inbound</code>, <code>{base}/twilio/voice/outbound</code>, <code>{base}/twilio/status</code>, <code>{base}/twilio/transcription</code>. Sync below sets them on each number.</p>
      </Card>

      {status.configured && (
        <Card padding={false} className="crm-section crm-settings-card">
          <div className="crm-section__head">
            <h3>Numbers</h3>
            <Button size="sm" variant="outline" iconLeft={<Icon name="refresh-cw" size={14} />} onClick={() => sync.mutate()} disabled={sync.isPending}>{sync.isPending ? 'Syncing…' : 'Sync from Twilio'}</Button>
          </div>
          <p className="crm-cell-muted">Every number on the Twilio account comes in here and gets its voice webhooks pointed at this site. Buy numbers in the Twilio Console, then sync.</p>
          {sync.data && <p className="crm-callout">{sync.data.added} added, {sync.data.updated} updated{sync.data.skipped ? `, ${sync.data.skipped} belong to another workspace` : ''}.</p>}
          <FormError error={sync.error} />
          {numbers.map((n) => <NumberCard key={n.id} n={n} meId={me?.userId ?? ''} />)}
          {!numbers.length && <p className="lead-none">No numbers yet. Sync from Twilio.</p>}
        </Card>
      )}
    </div>
  );
}

function NumberCard({ n, meId }: { n: PhoneNumber; meId: string }) {
  const qc = useQueryClient();
  const { members } = useMembers();
  const [f, setF] = useState({ label: n.label ?? '', purpose: n.purpose, greeting: n.greeting ?? '', outboundNotice: n.outboundNotice ?? '', ring: n.ringUserIds });
  const save = useMutation({
    mutationFn: () => updatePhoneNumber(n.id, { label: f.label, purpose: f.purpose, greeting: f.greeting, outboundNotice: f.outboundNotice, ringUserIds: f.ring }),
    onSuccess: () => { for (const k of ['phoneStatus', 'phoneNumbers']) void qc.invalidateQueries({ queryKey: [k] }); },
  });
  const toggleRing = (id: string) => setF((x) => {
    const cur = x.ring ?? members.map((m) => m.userId);
    const next = cur.includes(id) ? cur.filter((u) => u !== id) : [...cur, id];
    return { ...x, ring: next.length === members.length ? null : next };
  });
  return (
    <div className="crm-form sp-number">
      <div className="sp-number__head">
        <strong>{n.display}</strong>
        <span className="crm-cell-sub">{n.voice ? 'voice' : 'no voice'}{n.sms ? ' · SMS' : ''}{n.webhooksSetAt ? ` · webhooks set ${relativeTime(n.webhooksSetAt)}` : ' · webhooks not set'}</span>
      </div>
      <div className="crm-grid2">
        <Input label="Label" placeholder="Front desk" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <Select label="Used for" options={PURPOSES} value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value as PhoneNumber['purpose'] })} />
      </div>
      <Textarea label="Inbound greeting (the consent notice, said before the call connects)" rows={2} placeholder="Thanks for calling Triton Commerce. This call may be transcribed for quality. One moment while we connect you."
        value={f.greeting} onChange={(e) => setF({ ...f, greeting: e.target.value })} hint="Leave empty for the default. Counsel's wording goes here once approved." />
      <Textarea label="Outbound notice (said to the person as they answer)" rows={2} placeholder="This call from Triton Commerce may be transcribed for quality."
        value={f.outboundNotice} onChange={(e) => setF({ ...f, outboundNotice: e.target.value })} />
      <div>
        <span className="tr-field__label">Who rings {f.ring ? '' : '(everyone who is available)'}</span>
        <div className="pq-outcomes">
          {members.map((m) => {
            const on = !f.ring || f.ring.includes(m.userId);
            return <button key={m.userId} type="button" className={`pq-outcome ${on ? 'is-on' : ''}`} onClick={() => toggleRing(m.userId)}>{m.name ?? m.email}{m.userId === meId ? ' (you)' : ''}</button>;
          })}
        </div>
      </div>
      <div className="crm-record__actions">
        <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
      </div>
      <FormError error={save.error} />
    </div>
  );
}
